import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { routeByRole } from '@/utils/routeByRole';

const API = '/api';

/**
 * Shared Email OTP login form — Epic E.
 *
 * Two-step flow:
 *   1. Email input → POST /api/auth/email-otp/send
 *   2. 6-digit code input → POST /api/auth/email-otp/verify → session + routeByRole
 *
 * Props:
 *   roleHint    — "parent" | "student" (influences new-user role on verify)
 *   schoolSlug  — gates the send/verify to an invited school email
 *   accentClass — Tailwind color for buttons (e.g. "bg-orange-600")
 *   onBack      — optional callback when user clicks back from code screen
 *   lockedEmail — optional, if set the email field is pre-filled and disabled
 *                 (used by invite-accept flow)
 *   onVerified  — optional async callback called after successful OTP verify
 *                 with the user payload. Returning false prevents default
 *                 navigation (so callers can run their own flow first).
 */
export default function EmailOtpForm({
  roleHint = 'parent',
  schoolSlug,
  accentClass = 'bg-orange-600 hover:bg-orange-700',
  onBack,
  lockedEmail,
  onVerified,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState(lockedEmail || '');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState(null);
  const codeInputRef = useRef(null);

  // Resend cool-down tick
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Auto-focus code input when step changes
  useEffect(() => {
    if (step === 'code' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      const payload = { email: email.trim().toLowerCase(), role_hint: roleHint };
      if (schoolSlug) payload.school_slug = schoolSlug;
      const { data } = await axios.post(`${API}/auth/email-otp/send`, payload);
      toast.success(data.message || 'Code sent!');
      setStep('code');
      setCode('');
      setCooldown(60); // match backend OTP_RESEND_COOLDOWN_SEC
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Failed to send code. Please try again.';
      setError(detail);
      toast.error(detail);
      // On rate-limit (per-email cooldown, hourly cap, or IP limit) disable
      // the Send button for 30s so the user can't keep hammering it.
      if (status === 429) setCooldown(30);
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (codeValue) => {
    const otp = (codeValue ?? code).trim();
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError(null);
    setVerifying(true);
    try {
      const payload = { email: email.trim().toLowerCase(), otp, role_hint: roleHint };
      if (schoolSlug) payload.school_slug = schoolSlug;
      const { data } = await axios.post(`${API}/auth/email-otp/verify`, payload);
      if (data.session_token) {
        localStorage.setItem('yukti_session_token', data.session_token);
      }
      // Allow caller (e.g. invite-accept flow) to take over before routing
      if (typeof onVerified === 'function') {
        const shouldContinue = await onVerified(data);
        if (shouldContinue === false) return;
      }
      const target = routeByRole(data);
      navigate(target, { state: { user: data }, replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Verification failed. Please try again.';
      setError(detail);
      setCode('');
      codeInputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(v);
    setError(null);
    if (v.length === 6) {
      handleVerify(v);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    onBack?.();
  };

  // --- Render: email step ------------------------------------------------
  if (step === 'email') {
    return (
      <form onSubmit={handleSend} className="space-y-4" data-testid="email-otp-form">
        <label className="block">
          <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Email address
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            disabled={Boolean(lockedEmail)}
            data-testid="email-otp-email-input"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </label>
        {error && (
          <p className="text-sm text-red-600" data-testid="email-otp-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={sending || !email || cooldown > 0}
          data-testid="email-otp-send-button"
          className={`w-full ${accentClass} text-white py-3 rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {sending
            ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
            : cooldown > 0
              ? `Try again in ${cooldown}s`
              : 'Send code'}
        </button>
        <p className="text-xs text-gray-500 text-center">
          We'll email a 6-digit code. It expires in 10 minutes.
        </p>
      </form>
    );
  }

  // --- Render: code step -------------------------------------------------
  return (
    <div className="space-y-4" data-testid="email-otp-code-step">
      {!lockedEmail && (
        <button
          type="button"
          onClick={backToEmail}
          data-testid="email-otp-back-button"
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Change email
        </button>
      )}
      <div>
        <p className="text-sm text-gray-600 mb-3">
          Enter the 6-digit code sent to <span className="font-medium text-gray-900">{email}</span>
        </p>
        <input
          ref={codeInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          placeholder="• • • • • •"
          value={code}
          onChange={handleCodeChange}
          data-testid="email-otp-code-input"
          className="w-full px-4 py-4 text-center text-2xl tracking-[0.6em] font-mono rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
          disabled={verifying}
        />
      </div>
      {error && (
        <p className="text-sm text-red-600" data-testid="email-otp-error">{error}</p>
      )}
      <button
        type="button"
        onClick={() => handleVerify()}
        disabled={verifying || code.length !== 6}
        data-testid="email-otp-verify-button"
        className={`w-full ${accentClass} text-white py-3 rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {verifying ? (<><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>) : 'Verify & continue'}
      </button>
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={handleSend}
          disabled={cooldown > 0 || sending}
          data-testid="email-otp-resend-button"
          className="text-sm text-orange-600 hover:text-orange-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
    </div>
  );
}
